import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { AgingSummaryReportItem } from "types/Models/consolidatedReport";
import * as Yup from "yup";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";

type EditAgingSummaryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: AgingSummaryReportItem | null;
  onSave: (data: { noOfDays: number; noOfPeriods: number }) => void;
  modalTitle: string;
  submitText: string;
};

type FormValues = {
  noOfDays: string;
  noOfPeriods: string;
};

const validationSchema = Yup.object().shape({
  noOfDays: Yup.number()
    .required("Days per aging period is required")
    .min(1, "Must be at least 1 day")
    .max(360, "Must be at most 360 days"),

  noOfPeriods: Yup.number()
    .required("Number of aging periods is required")
    .min(1, "Must be at least 1 period")
    .max(12, "Must be at most 12 periods"),
});

export const AgingSummaryModal = ({
  isOpen,
  onClose,
  report,
  onSave,
  modalTitle,
  submitText,
}: EditAgingSummaryModalProps) => {
  const initialValues: FormValues = {
    noOfDays: String(report?.noOfDays || 30),
    noOfPeriods: String(report?.noOfPeriods || 6),
  };

  const handleSubmit = (values: FormValues) => {
    onSave({
      noOfDays: Number(values.noOfDays),
      noOfPeriods: Number(values.noOfPeriods),
    });
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular" asChild>
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleBlur }) => (
            <>
              <Modal.Header>
                <Modal.Title>{modalTitle}</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <TextInput
                    name="noOfDays"
                    label="Days per aging period"
                    placeholder="Enter no. of days"
                    value={values.noOfDays}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="number"
                    required
                    block
                    max={360}
                    min={1}
                  />
                  <TextInput
                    name="noOfPeriods"
                    label="Number of aging periods"
                    placeholder="Enter no. of periods"
                    value={values.noOfPeriods}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="number"
                    required
                    block
                    max={12}
                    min={1}
                  />
                </div>
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Button customType="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button customType="primary" type="submit">
                  {submitText}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
